// Глобальный imperative confirm — единый поп-ап подтверждения для деструктивных
// действий (удаление сущностей, очистка и т.п.). Любая поверхность вызывает
// `await confirm({...})` и получает boolean, не заводя локальный state + диалог.
//
// Хост (<ConfirmHost/>) смонтирован один раз в __root.tsx и рендерит
// ConfirmDialog от состояния этого стора. См. components/shared/confirm-host.tsx,
// hooks/use-confirm.ts.
import type { ReactNode } from "react";
import { create } from "zustand";

export interface ConfirmOptions {
	title: ReactNode;
	description?: ReactNode;
	/** Текст основной кнопки. По умолчанию common.actions.delete у хоста. */
	confirmLabel?: string;
	cancelLabel?: string;
	/** destructive красит кнопку в красный + рисует иконку корзины по умолчанию. */
	tone?: "default" | "destructive";
}

interface ConfirmState {
	open: boolean;
	options: ConfirmOptions | null;
	resolver: ((confirmed: boolean) => void) | null;
	/** Открыть диалог и дождаться выбора пользователя. */
	confirm: (options: ConfirmOptions) => Promise<boolean>;
	/** Подтвердить (нажата основная кнопка). */
	resolve: () => void;
	/** Отменить (нажата отмена / закрытие). */
	cancel: () => void;
}

export const useConfirmStore = create<ConfirmState>((set, get) => ({
	open: false,
	options: null,
	resolver: null,
	confirm: (options) =>
		new Promise<boolean>((resolve) => {
			// Если уже открыт другой запрос — отклоняем предыдущий, чтобы не зависнуть.
			const prev = get().resolver;
			prev?.(false);
			set({ open: true, options, resolver: resolve });
		}),
	resolve: () => {
		get().resolver?.(true);
		set({ open: false, resolver: null });
	},
	cancel: () => {
		get().resolver?.(false);
		set({ open: false, resolver: null });
	},
}));
